<template>
    <nav class="app-main-menu">
        <div
            :is="item.items ? 'app-main-menu-group' : 'app-main-menu-item'"
            v-for="item in items"
            v-show="item.isVisible"
            :key="item.label"
            v-bind="item"
            @open-paywall-modal="openPaywallModal"
        ></div>
    </nav>
</template>

<script>
    import AppMainMenuGroup from 'components/layouts/app-layout/AppMainMenuGroup'
    import AppMainMenuItem from 'components/layouts/app-layout/AppMainMenuItem'
    import UserMembershipStatusEnum from '../../../enums/UserMembershipStatusEnum'
    import Store from '~/store'

    export default {
        components: {
            AppMainMenuGroup,
            AppMainMenuItem,
        },

        computed: {
            items() {
                return [
                    {
                        route: { name: 'index.home' },
                        label: this.$t('layouts.app.main_menu.home'),
                        icon: 'menu-home',
                        isVisible:
                            this.$auth.isActiveMemberPhysician() ||
                            this.$auth.isPracticeAncillary() ||
                            this.$auth.isUserPracticeOther() ||
                            this.$auth.isUserPracticeDemo() ||
                            this.$auth.isUserTemporaryPhysician(),
                    },
                    {
                        route: { name: 'referrals.received' },
                        label: this.$t('layouts.app.main_menu.referrals'),
                        icon: 'menu-referrals',
                        showPaywall: true,
                        isVisible:
                            this.$auth.isActiveMemberPhysician() ||
                            this.$auth.isPracticeAncillary() ||
                            this.$auth.isUserPracticeOther() ||
                            this.$auth.isUserPracticeDemo() ||
                            this.$auth.isUserTemporaryPhysician(),
                        badge: this.getTotalReferralWithPendingActionCount(),
                    },
                    {
                        label: this.$t('layouts.app.main_menu.availability'),
                        icon: 'menu-availability',
                        showPaywall: true,
                        isVisible:
                            this.$auth.isActiveMemberPhysician() ||
                            this.$auth.isPracticeAncillary() ||
                            this.$auth.isUserPracticeDemo(),
                        items: this.$auth
                            .getUserLocations()
                            .filter((location) => location.isAvailabilitySet)
                            .map((location) => {
                                if (location.id === 0) {
                                    return {
                                        route: { name: 'availability', params: { action: 'schedule' }, matchFullRoute: true },
                                        label: this.$t('layouts.app.main_menu.availability_main_location'),
                                        showPaywall: true,
                                        badge: this.$auth.getActiveMember().newReferrals
                                            ? this.$auth
                                                  .getActiveMember()
                                                  .newReferrals.locations.filter((loc) => loc.practiceLocationId === 0)
                                                  .map((loc) => loc.referredToCount)[0]
                                            : undefined,
                                    }
                                } else {
                                    return {
                                        route: {
                                            name: 'availability',
                                            params: { action: 'schedule' },
                                            query: { locationId: String(location.id) },
                                            matchFullRoute: true,
                                        },
                                        label: location.name,
                                        showPaywall: true,
                                        highlight: location.name,
                                        badge: this.$auth.getActiveMember().newReferrals
                                            ? this.$auth
                                                  .getActiveMember()
                                                  .newReferrals.locations.filter((loc) => loc.practiceLocationId === location.id)
                                                  .map((loc) => loc.referredToCount)[0]
                                            : undefined,
                                    }
                                }
                            }),
                    },
                    {
                        label: this.$t('layouts.app.main_menu.network'),
                        icon: 'menu-network',
                        showPaywall: true,
                        isVisible:
                            this.$auth.isActiveMemberPhysician() ||
                            this.$auth.isPracticeAncillary() ||
                            this.$auth.isUserPracticeDemo() ||
                            this.$auth.isUserPracticeOther(),
                        route: { name: 'network.connections' },
                    },
                    {
                        label: this.$t('layouts.app.main_menu.claim_your_profile'),
                        icon: 'menu-network',
                        isVisible: this.$auth.isUserAdministrator(),
                        route: { name: 'admin.claim-your-profile' },
                    },
                    {
                        label: 'Manage License Status',
                        icon: 'menu-network',
                        isVisible: this.$auth.isUserAdministrator(),
                        route: { name: 'admin.manage-license-status' },
                    },
                    {
                        label: this.$t('pages.administrator.outreach_tasks.menu_label'),
                        icon: 'menu-network',
                        isVisible: this.$auth.isUserAdministrator(),
                        route: { name: 'admin.outreach-tasks' },
                    },
                    {
                        label: this.$t('pages.administrator.leads.menu_label'),
                        icon: 'menu-network',
                        isVisible: this.$auth.isUserAdministrator(),
                        items: [
                            {
                                route: { name: 'admin.leads.referral-leakage-audit' },
                                label: this.$t('pages.administrator.leads.referral_leakage_audit.menu_label'),
                            },
                        ],
                    },
                    {
                        label: this.$t('layouts.app.main_menu.network_intelligence'),
                        icon: 'menu-referrals',
                        isVisible: this.$auth.isUserAdministrator(),
                        items: [
                            {
                                route: { name: 'intelligence.overview' },
                                label: this.$t('pages.intelligence.nav.overview'),
                            },
                            {
                                route: { name: 'intelligence.constraints' },
                                label: this.$t('pages.intelligence.nav.constraints'),
                            },
                            {
                                route: { name: 'intelligence.delivery' },
                                label: this.$t('pages.intelligence.nav.delivery'),
                            },
                            {
                                route: { name: 'intelligence.supply-demand' },
                                label: this.$t('pages.intelligence.nav.supply_demand'),
                            },
                        ],
                    },
                    {
                        label: this.$t('layouts.app.main_menu.governance'),
                        icon: 'menu-network',
                        isVisible: this.$auth.isUserAdministrator(),
                        route: { name: 'governance.overview' },
                    },
                    {
                        label: this.$t('layouts.app.main_menu.mma_invites_member'),
                        icon: 'menu-network',
                        showPaywall: true,
                        isVisible: this.$auth.isUserAdministrator(),
                        route: { name: 'admin.invites-member' },
                        items: [
                            {
                                route: { name: 'admin.invite-by-email' },
                                label: 'Invite by email',
                                showPaywall: true,
                            },
                            {
                                route: { name: 'admin.invite-by-phone' },
                                label: 'Invite by phone',
                                showPaywall: true,
                            },
                        ],
                    },
                    {
                        label: this.$t('layouts.app.main_menu.practice'),
                        icon: 'menu-practice',
                        showPaywall: true,
                        isVisible: this.$auth.getUser().isMasterUser || this.$auth.isUserPracticeDemo(),
                        items: [
                            {
                                route: { name: 'practice.members' },
                                label: this.$t('layouts.app.main_menu.practice_members'),
                                showPaywall: true,
                            },
                            {
                                route: {
                                    name:
                                        this.$auth.getPractice().practiceType === 'ancillary'
                                            ? 'practice.profile-ancillary'
                                            : 'practice.profile',
                                },
                                showPaywall: true,
                                label: this.$t('layouts.app.main_menu.practice_profile'),
                            },
                            {
                                route: { name: 'practice.billing' },
                                label: this.$t('layouts.app.main_menu.plan_billing'),
                                showPaywall: true,
                                isHidden: this.$auth.isPracticeOther(),
                            },
                        ],
                        badge: this.$auth
                            .getMembers()
                            .filter((member) => member.practiceMembershipStatus === UserMembershipStatusEnum.PENDING).length,
                    },
                    {
                        label: this.$t('layouts.app.main_menu.patients'),
                        icon: 'menu-network',
                        isVisible:
                            this.$auth.isActiveMemberPhysician() ||
                            this.$auth.isPracticeAncillary() ||
                            this.$auth.isUserPracticeOther(),
                        items: [
                            {
                                route: {
                                    name: `patients.list.patients`,
                                },
                                label: this.$t('layouts.app.main_menu.patient_list'),
                            },
                            {
                                route: {
                                    name: `patients.upload.patients`,
                                },
                                label: this.$t('layouts.app.main_menu.upload'),
                            },
                        ],
                    },
                    {
                        route: { name: 'public.shared-records', params: { token: this.$route.params.token } },
                        label: this.$t('layouts.app.main_menu.shared_records'),
                        icon: 'menu-referrals',
                        isVisible: this.$route.name === 'public.shared-records',
                    },
                ]
            },
        },

        methods: {
            openPaywallModal() {
                this.emitEvent({ name: 'open-paywall-modal' })
            },

            getTotalReferralWithPendingActionCount() {
                if (this.$auth.isUserTemporaryPhysician()) {
                    return 1
                }
                return (
                    Store.getters['auth/getProviderPendingReferralActionCount'] +
                    Store.getters['auth/getPatientPendingReferralActionCount']
                )
            },
        },
    }
</script>
